import React, { useState, useEffect } from 'react';
import { getUserPayments } from '../services/paymentService';
import { getUserInvoices, downloadInvoice, printInvoice } from '../services/invoiceService';
import { formatAmount } from '../services/razorpayService';
import LoadingSpinner from '../components/LoadingSpinner';

const BillingHistory = ({ user, userData }) => {
    const [payments, setPayments] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('invoices'); // 'invoices' or 'payments'

    useEffect(() => {
        if (user?.uid) {
            fetchData();
        }
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch payments
            const paymentsResult = await getUserPayments(user.uid);
            if (paymentsResult.success) {
                setPayments(paymentsResult.payments);
            }

            // Fetch invoices
            const invoicesResult = await getUserInvoices(user.uid);
            if (invoicesResult.success) {
                setInvoices(invoicesResult.invoices);
            }
        } catch (error) {
            console.error('Error fetching billing data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            success: { bg: 'bg-green-50 border-green-200 text-green-700', label: 'Authorized' },
            pending: { bg: 'bg-amber-50 border-amber-200 text-amber-700', label: 'Processing' },
            failed: { bg: 'bg-red-50 border-red-200 text-red-700', label: 'Declined' },
            refunded: { bg: 'bg-slate-100 border-slate-300 text-slate-600', label: 'Reversed' },
        };

        const config = statusConfig[status] || statusConfig.pending;
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${config.bg}`}>
                {config.label}
            </span>
        );
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) return <LoadingSpinner />;

    const successfulPayments = payments.filter(p => p.status === 'success');
    const totalOutlay = successfulPayments.reduce((sum, p) => sum + (p.totalAmount || 0), 0);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-100 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                
                {/* Header */}
                <div className="mb-10 flex flex-col md:flex-row md:items-center md:justify-between border-b border-slate-200/80 pb-6">
                    <div>
                        <h1 className="text-3xl font-black text-[#0E3A61] tracking-tight mb-2">Financial Ledger</h1>
                        <p className="text-slate-600 font-medium text-sm">
                            Track system ledger entries, access printable invoices, and view premium plan assets.
                        </p>
                    </div>
                    <div className="mt-4 md:mt-0 flex gap-2">
                        <button
                            onClick={fetchData}
                            className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm"
                        >
                            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh Log
                        </button>
                    </div>
                </div>

                {/* Summary Metrics (At the Top) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    {/* Authorized Payments Card */}
                    <div className="bg-white border border-slate-200/70 rounded-2xl rounded-tr-[2.5rem] rounded-bl-[2.5rem] shadow-sm p-6 flex items-center justify-between hover:shadow-md transition-all duration-300 group">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center border border-emerald-100 group-hover:scale-110 transition-transform">
                                <svg className="w-7 h-7 text-[#508D0C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Authorized Payments</p>
                                <p className="text-3xl font-black text-slate-800">
                                    {successfulPayments.length}
                                </p>
                            </div>
                        </div>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                            Clear
                        </span>
                    </div>

                    {/* Total Outlay (Highlighted Premium Card) */}
                    <div className="bg-gradient-to-br from-[#0E3A61] to-[#154e80] rounded-2xl rounded-tr-[2.5rem] rounded-bl-[2.5rem] shadow-md p-6 flex items-center justify-between hover:shadow-lg transition-all duration-300 group border border-[#0E3A61]">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center border border-white/20 group-hover:scale-110 transition-transform">
                                <svg className="w-7 h-7 text-[#E2822F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-white/70 uppercase tracking-wider mb-1">Aggregated Outlay</p>
                                <p className="text-3xl font-black text-white">
                                    {formatAmount(totalOutlay)}
                                </p>
                            </div>
                        </div>
                        <span className="text-[10px] font-bold text-[#E2822F] bg-white/10 px-2.5 py-1 rounded-full border border-white/10">
                            INR Total
                        </span>
                    </div>

                    {/* Total Invoices Card */}
                    <div className="bg-white border border-slate-200/70 rounded-2xl rounded-tr-[2.5rem] rounded-bl-[2.5rem] shadow-sm p-6 flex items-center justify-between hover:shadow-md transition-all duration-300 group">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 group-hover:scale-110 transition-transform">
                                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Generated Ledgers</p>
                                <p className="text-3xl font-black text-slate-800">
                                    {invoices.length}
                                </p>
                            </div>
                        </div>
                        <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
                            PDF Docs
                        </span>
                    </div>
                </div>

                {/* Tab Controls (Segmented Control Pill Style) */}
                <div className="flex justify-center mb-8">
                    <div className="bg-slate-200/60 p-1.5 rounded-2xl flex w-full max-w-md border border-slate-200 shadow-inner">
                        <button
                            onClick={() => setActiveTab('invoices')}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl transition-all duration-300 ${activeTab === 'invoices'
                                ? 'bg-[#0E3A61] text-white shadow-md'
                                : 'text-slate-600 hover:text-slate-800'
                                }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Tax Statements ({invoices.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('payments')}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl transition-all duration-300 ${activeTab === 'payments'
                                ? 'bg-[#0E3A61] text-white shadow-md'
                                : 'text-slate-600 hover:text-slate-800'
                                }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                            Clearing Logs ({payments.length})
                        </button>
                    </div>
                </div>

                {/* Invoices Tab */}
                {activeTab === 'invoices' && (
                    <div className="grid grid-cols-1 gap-6">
                        {invoices.length === 0 ? (
                            <div className="bg-white rounded-3xl rounded-tr-[4rem] rounded-bl-[4rem] border border-slate-200/80 shadow-md p-16 text-center max-w-2xl mx-auto w-full">
                                <div className="w-20 h-20 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-black text-slate-800 mb-2">No Financial Statements Available</h3>
                                <p className="text-slate-500 font-medium text-sm max-w-md mx-auto">
                                    Once your subscription plan is active, all ledger invoices will be generated and displayed here.
                                </p>
                            </div>
                        ) : (
                            invoices.map((invoice) => (
                                <div
                                    key={invoice.id}
                                    className="bg-white rounded-3xl rounded-tr-[3.5rem] rounded-bl-[3.5rem] border border-slate-200/70 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-300 p-6 sm:p-8"
                                >
                                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                                        <div className="flex-1">
                                            
                                            {/* Card Top Row */}
                                            <div className="flex flex-wrap items-center gap-3 mb-6">
                                                <div className="w-12 h-12 bg-gradient-to-br from-[#0E3A61] to-[#1a5f97] rounded-xl flex items-center justify-center shadow-sm">
                                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-black text-[#0E3A61] tracking-tight">
                                                        {invoice.invoiceNumber}
                                                    </h3>
                                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                                        Date: {formatDate(invoice.invoiceDate)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Details Section */}
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b border-slate-100">
                                                <div>
                                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Operational Plan</p>
                                                    <p className="font-bold text-slate-800 text-sm">{invoice.packageName}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Access Validity</p>
                                                    <p className="font-bold text-slate-800 text-sm">{invoice.packageDuration} Days</p>
                                                </div>
                                                <div className="col-span-2">
                                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Reference Hash</p>
                                                    <p className="font-mono text-xs text-slate-500 font-semibold truncate bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                                        {invoice.razorpayPaymentId}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Cost Breakdown Panel */}
                                            <div className="bg-gradient-to-br from-slate-50/50 via-blue-50/5 to-slate-50 rounded-2xl p-5 border border-slate-100">
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                    <div className="flex justify-between sm:flex-col gap-1">
                                                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Net Rate</span>
                                                        <span className="font-bold text-slate-700">
                                                            {formatAmount(invoice.baseAmount)}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between sm:flex-col gap-1 border-t sm:border-t-0 sm:border-l border-slate-200/60 pt-3 sm:pt-0 sm:pl-4">
                                                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">GST ({invoice.gstRate || 18}%)</span>
                                                        <span className="font-bold text-slate-700">
                                                            {formatAmount(invoice.gstAmount)}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between sm:flex-col gap-1 border-t sm:border-t-0 sm:border-l border-[#0E3A61]/20 pt-3 sm:pt-0 sm:pl-4">
                                                        <span className="text-xs font-bold text-[#0E3A61] uppercase tracking-wider">Gross Total</span>
                                                        <span className="text-xl font-black bg-gradient-to-r from-[#0E3A61] to-[#E2822F] bg-clip-text text-transparent">
                                                            {formatAmount(invoice.totalAmount)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>

                                        {/* Actions */}
                                        <div className="flex flex-row lg:flex-col gap-3 lg:w-44 w-full justify-end border-t lg:border-t-0 border-slate-100 pt-4 lg:pt-0">
                                            <button
                                                onClick={() => downloadInvoice(invoice)}
                                                className="flex-1 lg:flex-none justify-center px-4 py-3 bg-[#0E3A61] hover:bg-[#154e80] text-white rounded-xl transition-all duration-200 flex items-center gap-2 text-sm font-bold shadow-sm"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                Export PDF
                                            </button>
                                            <button
                                                onClick={() => printInvoice(invoice)}
                                                className="flex-1 lg:flex-none justify-center px-4 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all duration-200 flex items-center gap-2 text-sm font-bold shadow-sm"
                                            >
                                                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                                </svg>
                                                Print Document
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Payments Tab */}
                {activeTab === 'payments' && (
                    <div className="bg-white rounded-3xl rounded-tr-[3.5rem] rounded-bl-[3.5rem] border border-slate-200/80 shadow-sm overflow-hidden">
                        {payments.length === 0 ? (
                            <div className="p-16 text-center max-w-2xl mx-auto">
                                <div className="w-20 h-20 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                                    <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-black text-slate-800 mb-2">Zero Settlements Logged</h3>
                                <p className="text-slate-500 font-medium text-sm max-w-md mx-auto">
                                    Your clearing logs will automatically appear here once transaction records are processed.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead className="bg-[#0E3A61] text-white">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                Settlement Date
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                Hiring Plan
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                Subtotal
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                Tax Contribution
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                Aggregate Total
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                Clearance Status
                                            </th>
                                            <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                Gateway Reference
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {payments.map((payment) => (
                                            <tr key={payment.id} className="hover:bg-slate-50/60 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-700">
                                                    {formatDate(payment.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-bold text-slate-800">
                                                        {payment.packageName}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-600">
                                                    {formatAmount(payment.baseAmount || (payment.totalAmount ? payment.totalAmount / 1.18 : 0))}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-600">
                                                    {formatAmount(payment.gstAmount || 0)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-800">
                                                    {formatAmount(payment.totalAmount)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(payment.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 font-mono font-semibold">
                                                    {payment.razorpayPaymentId || 'N/A'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
                
            </div>
        </div>
    );
};

export default BillingHistory;
